import { getCategoricalLabelsFromInputChain } from "./components/categorical_label_chain"
import type { CategoricalLabelRow } from "./components/categorical_label_chain"
import { booleanDataSocket } from "./socket_types"
import { BooleanTileGrid, CategoricalTileGrid } from "./tile_grid"

export type PayoffAxisEntry = {
  value: number
  label: string
  kind: "binary" | "categorical"
}

export type PayoffAxesConfig = {
  a: PayoffAxisEntry[]
  b: PayoffAxisEntry[]
}

/** Display order matches legacy 2×2 UI: True row/column before False. */
export const BINARY_AXIS_ENTRIES: PayoffAxisEntry[] = [
  { value: 1, label: "True", kind: "binary" },
  { value: 0, label: "False", kind: "binary" },
]

export const PAYOFF_AXES_DATA_KEY = "payoffAxes"

const LEGACY_PAYOFF_BY_CELL: Record<string, string> = {
  "1,1": "payoff_tt",
  "1,0": "payoff_tf",
  "0,1": "payoff_ft",
  "0,0": "payoff_ff",
}

export function defaultBinaryPayoffAxes(): PayoffAxesConfig {
  return {
    a: BINARY_AXIS_ENTRIES.map(e => ({ ...e })),
    b: BINARY_AXIS_ENTRIES.map(e => ({ ...e })),
  }
}

export function payoffCellKey(aValue: number, bValue: number): string {
  return `payoff_a${aValue}_b${bValue}`
}

export function isPureBinaryAxes(axes: PayoffAxesConfig): boolean {
  const isBinary = (entries: PayoffAxisEntry[]) =>
    entries.length === 2 &&
    entries.every(e => e.kind === "binary" && (e.value === 0 || e.value === 1))
  return isBinary(axes.a) && isBinary(axes.b)
}

export function axesFromInputGrid(grid: BooleanTileGrid | CategoricalTileGrid): PayoffAxisEntry[] {
  if (grid instanceof BooleanTileGrid) {
    return BINARY_AXIS_ENTRIES.map(e => ({ ...e }))
  }

  const labels = grid.labels ?? new Map<number, string>()
  const entries: PayoffAxisEntry[] = []
  labels.forEach((label, value) => {
    if (value === 255) return
    const text = typeof label === "string" ? label.trim() : ""
    entries.push({
      value,
      label: text || `Category ${value}`,
      kind: "categorical",
    })
  })
  entries.sort((x, y) => x.value - y.value)
  return entries
}

export function axesFingerprint(axes: PayoffAxesConfig): string {
  const fmt = (entries: PayoffAxisEntry[]) =>
    entries.map(e => `${e.kind}:${e.value}:${e.label}`).join("|")
  return `${fmt(axes.a)};;${fmt(axes.b)}`
}

export function readPayoffAxesFromNodeData(data: Record<string, unknown> | undefined): PayoffAxesConfig {
  const raw = data?.[PAYOFF_AXES_DATA_KEY]
  if (!raw || typeof raw !== "object") {
    return defaultBinaryPayoffAxes()
  }
  const obj = raw as { a?: unknown; b?: unknown }
  const parseSide = (side: unknown): PayoffAxisEntry[] | null => {
    if (!Array.isArray(side) || side.length === 0) return null
    const entries: PayoffAxisEntry[] = []
    for (const item of side) {
      if (!item || typeof item !== "object") return null
      const row = item as Record<string, unknown>
      const value = Number(row.value)
      const label = String(row.label ?? "").trim()
      const kind = row.kind === "categorical" ? "categorical" : "binary"
      if (!Number.isFinite(value) || !label) return null
      entries.push({ value, label, kind })
    }
    return entries
  }
  const a = parseSide(obj.a)
  const b = parseSide(obj.b)
  if (!a || !b) return defaultBinaryPayoffAxes()
  return { a, b }
}

export function writePayoffAxesToNodeData(data: Record<string, unknown>, axes: PayoffAxesConfig): void {
  data[PAYOFF_AXES_DATA_KEY] = {
    a: axes.a.map(e => ({ ...e })),
    b: axes.b.map(e => ({ ...e })),
  }
}

export function axisEntriesFromLabelRows(rows: CategoricalLabelRow[]): PayoffAxisEntry[] {
  return rows.map(row => ({
    value: row.value,
    label: row.label,
    kind: "categorical" as const,
  }))
}

type InputPortLike = { connections?: Array<{ output?: { socket?: unknown } }> }

function getInputPort(
  node: { inputs: Map<string, InputPortLike> | Record<string, InputPortLike> },
  inputKey: string
): InputPortLike | undefined {
  const inputsAny = node.inputs
  if (typeof (inputsAny as Map<string, InputPortLike>).get === "function") {
    return (inputsAny as Map<string, InputPortLike>).get(inputKey)
  }
  return (inputsAny as Record<string, InputPortLike>)[inputKey]
}

function inputPortHasConnection(
  node: { inputs: Map<string, InputPortLike> | Record<string, InputPortLike> },
  inputKey: string
): boolean {
  const inputPort = getInputPort(node, inputKey)
  return (inputPort?.connections?.length ?? 0) > 0
}

function connectedOutputSocket(
  node: { inputs: Map<string, InputPortLike> | Record<string, InputPortLike> },
  inputKey: string
): unknown {
  const inputPort = getInputPort(node, inputKey)
  return inputPort?.connections?.[0]?.output?.socket
}

/** Infer axis labels/sizes from the graph when connections change (before process). */
export function axisEntriesFromInputConnection(editor: unknown, node: unknown, inputKey: string): PayoffAxisEntry[] {
  if (!editor || !node) {
    return BINARY_AXIS_ENTRIES.map(e => ({ ...e }))
  }
  const nodeLike = node as { inputs: Map<string, InputPortLike> | Record<string, InputPortLike> }
  if (!inputPortHasConnection(nodeLike, inputKey)) {
    return BINARY_AXIS_ENTRIES.map(e => ({ ...e }))
  }

  // Wired directly to a boolean dataset output → 2×2 axis (do not walk label chain).
  if (connectedOutputSocket(nodeLike, inputKey) === booleanDataSocket) {
    return BINARY_AXIS_ENTRIES.map(e => ({ ...e }))
  }

  const rows = getCategoricalLabelsFromInputChain(editor, node, inputKey)
  if (rows.length > 0) {
    return axisEntriesFromLabelRows(rows)
  }
  return BINARY_AXIS_ENTRIES.map(e => ({ ...e }))
}

export function inferPayoffAxesFromConnections(editor: unknown, node: unknown): PayoffAxesConfig {
  return {
    a: axisEntriesFromInputConnection(editor, node, "a"),
    b: axisEntriesFromInputConnection(editor, node, "b"),
  }
}

/** Writes axes when the fingerprint changed; returns whether the matrix shape changed. */
export function applyPayoffAxesToNodeData(
  data: Record<string, unknown>,
  axes: PayoffAxesConfig
): boolean {
  const nextFp = axesFingerprint(axes)
  if (data._payoffAxesFingerprint === nextFp) {
    return false
  }
  writePayoffAxesToNodeData(data, axes)
  data._payoffAxesFingerprint = nextFp
  return true
}

export function readCellValue(
  grid: BooleanTileGrid | CategoricalTileGrid,
  x: number,
  y: number,
  zoom: number
): number {
  if (grid instanceof BooleanTileGrid) {
    return grid.get(x, y, zoom) ? 1 : 0
  }
  return grid.get(x, y, zoom)
}

function parsePayoffRaw(raw: unknown): number {
  if (raw === undefined || raw === null) return NaN
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : NaN
  }
  if (typeof raw === "string") {
    const s = raw.trim()
    if (s === "" || s === "-" || s === "." || s === "-.") return NaN
    const parsed = Number(s)
    return Number.isNaN(parsed) ? NaN : parsed
  }
  return NaN
}

export function payoffFromNodeData(
  data: Record<string, unknown>,
  aValue: number,
  bValue: number,
  axes?: PayoffAxesConfig
): number {
  const key = payoffCellKey(aValue, bValue)
  let raw = data[key]
  if (raw === undefined && axes && isPureBinaryAxes(axes)) {
    const legacy = LEGACY_PAYOFF_BY_CELL[`${aValue},${bValue}`]
    if (legacy) raw = data[legacy]
  }
  return parsePayoffRaw(raw)
}

export function lookupPayoffForCells(
  aValue: number,
  bValue: number,
  data: Record<string, unknown>,
  axes: PayoffAxesConfig
): number {
  if (aValue === 255 || bValue === 255) return NaN
  return payoffFromNodeData(data, aValue, bValue, axes)
}
